import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { CIDADES_POR_REGIAO, TODAS_AS_CIDADES, type CidadeResumo } from './data';

/**
 * Índice das cidades atendidas.
 *
 * É o destino do link "Cidades" que aparece na trilha de navegação de todas as
 * páginas de cidade. Espelha a estrutura da página /locais.
 *
 * Os cards não usam foto de propósito: as imagens do site ainda são servidas
 * pela Readdy e a intenção é sair de lá, então esta página nova não acrescenta
 * dependência nenhuma a esse serviço.
 */

function CidadeCard({ cidade, index }: { cidade: CidadeResumo; index: number }) {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <Link
      to={cidade.rota}
      ref={ref as never}
      className={`group flex flex-col bg-white rounded-3xl border border-gray-100 p-7 transition-all duration-700 hover:border-primary/30 hover:-translate-y-1 cursor-pointer ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
      style={{ transitionDelay: `${Math.min(index, 8) * 80}ms` }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <h3 className="font-heading font-bold text-secondary text-xl leading-tight">
          {cidade.nome}
        </h3>
        <span className="shrink-0 inline-block bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          {cidade.uf}
        </span>
      </div>

      <p className="font-body text-sm text-gray-500 leading-relaxed mb-6 flex-1">
        {cidade.descricao}
      </p>

      <div className="flex items-center justify-end border-t border-gray-100 pt-4">
        <span className="inline-flex items-center gap-1.5 font-body font-semibold text-sm text-primary group-hover:gap-2.5 transition-all">
          Ver palestra silenciosa em {cidade.nome}
          <i className="ri-arrow-right-line text-primary text-sm"></i>
        </span>
      </div>
    </Link>
  );
}

export default function CidadesHubPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: introRef, isVisible: introVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation({ threshold: 0.2 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'Palestra Silenciosa no Brasil | Cidades Atendidas pela STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'A STIB atende palestra silenciosa em 19 cidades do Brasil — São Paulo, Rio, Brasília, BH, Curitiba, Porto Alegre, Recife, Salvador, Fortaleza e mais. Veja a página da sua cidade.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${siteUrl}/cidades`);

    const schemas = [
      {
        id: 'cidades-hub-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Cidades', item: `${siteUrl}/cidades` },
          ],
        },
      },
      {
        id: 'cidades-hub-itemlist',
        data: {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'Cidades atendidas pela STIB Palestra Silenciosa',
          description:
            'Cidades do Brasil onde a STIB fornece palestra silenciosa com fones sem fio para eventos corporativos.',
          numberOfItems: TODAS_AS_CIDADES.length,
          itemListElement: TODAS_AS_CIDADES.map((cidade, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: `Palestra Silenciosa ${cidade.nome}`,
            item: `${siteUrl}${cidade.rota}`,
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existente = document.getElementById(id);
      if (existente) existente.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-[60vh] w-full flex items-center justify-center overflow-hidden mt-[72px] bg-gradient-to-br from-secondary via-secondary to-secondary/85"
      >
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full py-24">
          <div
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            cidades atendidas
          </div>

          <header>
            <h1
              className={`font-heading font-extrabold text-4xl md:text-6xl text-white mb-6 tracking-tight leading-tight lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              palestra silenciosa em todo o brasil
            </h1>
          </header>

          <p
            className={`font-body font-light text-lg md:text-xl text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            A <strong className="text-white">STIB</strong> leva fones sem fio, técnico e estrutura
            completa para eventos corporativos em {TODAS_AS_CIDADES.length} cidades. Escolha a sua e
            veja os locais, os casos e o orçamento da sua região.
          </p>

          <div
            className={`flex flex-wrap items-center justify-center gap-6 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">{TODAS_AS_CIDADES.length}</p>
              <p className="font-body text-white/60 text-sm">cidades atendidas</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">5.000+</p>
              <p className="font-body text-white/60 text-sm">fones em estoque</p>
            </div>
            <div className="w-px h-10 bg-white/20"></div>
            <div className="text-center">
              <p className="font-heading font-bold text-primary text-3xl">500+</p>
              <p className="font-body text-white/60 text-sm">eventos realizados</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTRO ────────────────────────────────────────────── */}
      <section ref={introRef} className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2
            className={`font-heading font-bold text-4xl md:text-5xl text-secondary lowercase mb-8 transition-all duration-700 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            atendimento onde o seu evento acontece
          </h2>
          <p
            className={`font-body text-lg text-gray-600 leading-relaxed mb-6 transition-all duration-700 delay-100 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Cada cidade tem a sua página, com os espaços de evento da região, os casos que já
            realizamos por lá e um formulário de orçamento direto. É o caminho mais curto entre a
            sua data e uma proposta.
          </p>
          <p
            className={`font-body text-lg text-gray-600 leading-relaxed transition-all duration-700 delay-200 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Não achou a sua cidade na lista? A <strong className="text-secondary">STIB</strong>{' '}
            atende o Brasil inteiro — fale com a gente que montamos a logística.
          </p>

          <p
            className={`font-body text-base text-gray-500 leading-relaxed mt-6 transition-all duration-700 delay-300 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            Nossa sede fica em São Paulo Capital e temos filial em Brasília, DF. Em Recife, Rio de Janeiro e Campinas, equipamentos e profissionais estão presentes na cidade, sem escritório físico. Nas demais cidades e estados desta página não temos filiais nem escritórios — o atendimento é feito por equipe e equipamentos que se deslocam a partir de São Paulo até o local do seu evento.
          </p>
        </div>
      </section>

      {/* ── CIDADES POR REGIÃO ───────────────────────────────── */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          {CIDADES_POR_REGIAO.map((grupo, gi) => (
            <div key={grupo.regiao} className={gi > 0 ? 'mt-20' : ''}>
              <div className="mb-10">
                <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-3 block">
                  {grupo.cidades.length} {grupo.cidades.length === 1 ? 'cidade' : 'cidades'}
                </span>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-secondary lowercase">
                  {grupo.regiao}
                </h2>
                <p className="font-body text-gray-500 mt-3 max-w-2xl">{grupo.chamada}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {grupo.cidades.map((cidade, index) => (
                  <CidadeCard key={cidade.rota} cidade={cidade} index={index} />
                ))}
              </div>

              {grupo.regiao === 'São Paulo' && (
                <div className="mt-8 text-center">
                  <Link
                    to="/sao-paulo-estado"
                    className="inline-flex items-center gap-2 font-body font-semibold text-sm text-primary hover:gap-3 transition-all cursor-pointer"
                  >
                    Ver a visão geral do estado de São Paulo
                    <i className="ri-arrow-right-line text-primary text-sm"></i>
                  </Link>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA FINAL ────────────────────────────────────────── */}
      <section ref={ctaRef} className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-secondary to-secondary/90"></div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2
            className={`font-heading font-bold text-4xl md:text-5xl text-white lowercase mb-6 transition-all duration-700 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            sua cidade não está na lista?
          </h2>
          <p
            className={`font-body text-lg text-white/80 mb-10 max-w-2xl mx-auto leading-relaxed transition-all duration-700 delay-100 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            A STIB atende <strong>eventos em todo o Brasil</strong>. Conte onde e quando é o seu
            evento que montamos a logística e enviamos o orçamento.
          </p>
          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <i className="ri-arrow-right-line text-white text-lg"></i>
            </a>
            <a
              href="https://wa.me/5511917776227?text=Olá! Vi a página de cidades no site e gostaria de solicitar um orçamento para meu evento."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
